import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import OnboardingGuideHeader from '@/components/ui/OnboardingGuideHeader';
import { isOnboardingSectionCompleted, setOnboardingLastStep } from '@/utils/onboardingProgress';
import { analyticsService } from '@/services/analyticsService';
import {
  clearDeferredOnboardingReturn,
  getDeferredOnboardingReturn,
  getDeferredOnboardingUrl,
  saveDeferredOnboardingReturn,
} from '@/utils/onboardingDeferredReturn';

const ONBOARDING_STEPS: Record<string, { name: string; index: number }> = {
  '/onboarding/topics': { name: 'topics', index: 1 },
  '/onboarding/books': { name: 'book_selection', index: 2 },
  '/onboarding/read': { name: 'nonfiction_reading', index: 3 },
  '/onboarding/novel-read': { name: 'novel_reading', index: 3 },
  '/onboarding/xp-claim': { name: 'xp_claim', index: 4 },
  '/onboarding/streak': { name: 'streak', index: 5 },
  '/onboarding/achievement': { name: 'achievement', index: 4 },
  '/onboarding/phone': { name: 'phone', index: 6 },
  '/onboarding/otp-verification': { name: 'otp_verification', index: 7 },
  '/onboarding/name': { name: 'name', index: 8 },
};

const getOnboardingProgressStep = (path: string): number => {
  if (path === '/onboarding/topics') return 1;
  if (path === '/onboarding/books') return 2;
  if (path === '/onboarding/read' || path === '/onboarding/novel-read') return 3;
  if (path === '/onboarding/xp-claim' || path === '/onboarding/achievement') return 4;
  if (path === '/onboarding/streak') return 5;
  if (path === '/onboarding/phone') return 6;
  if (path === '/onboarding/otp-verification') return 7;
  if (path === '/onboarding/name') return 8;
  return 1;
};

const OnboardingLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [headerHeight, setHeaderHeight] = useState(128);
  const [readGuideText, setReadGuideText] = useState('');
  const [readGuideReplayKey, setReadGuideReplayKey] = useState(0);
  const [initialRoute] = useState(location.pathname);
  const [initialNavigationType] = useState(() =>
    (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)?.type,
  );

  const topic = new URLSearchParams(location.search).get('topic') || sessionStorage.getItem('onboarding_topic_name') || '';
  const stateBookId = (location.state as any)?.bookId;
  const savedBookId = localStorage.getItem('onboarding_book_id');
  const currentBookId = stateBookId ?? (savedBookId ? Number(savedBookId) : undefined);

  const route = location.pathname;
  const onboardingStep = ONBOARDING_STEPS[route];
  const isOnboardingRead = route.startsWith('/onboarding/read');
  const isOnboardingNovelRead = route.startsWith('/onboarding/novel-read');
  const isOnboardingXpClaim = route.startsWith('/onboarding/xp-claim');
  const isOnboardingStreak = route.startsWith('/onboarding/streak');
  const isOnboardingAchievement = route.startsWith('/onboarding/achievement');
  const onboardingCompleted = isOnboardingSectionCompleted();
  const isOnboardingReward = isOnboardingXpClaim || isOnboardingStreak || isOnboardingAchievement;
  const normalizedGuideText = (readGuideText || '').trim();
  const deferredReturn = getDeferredOnboardingReturn();
  const canDeferRegistration = [
    '/onboarding/topics',
    '/onboarding/books',
    '/onboarding/read',
    '/onboarding/novel-read',
    '/onboarding/xp-claim',
    '/onboarding/streak',
    '/onboarding/achievement',
  ].some((path) => route === path || route.startsWith(`${path}/`));

  const returnFromPhone = () => {
    const savedReturn = getDeferredOnboardingReturn();
    if (!savedReturn) return;
    clearDeferredOnboardingReturn();
    navigate(getDeferredOnboardingUrl(savedReturn), {
      replace: true,
      state: savedReturn.state,
    });
  };

  const deferRegistration = () => {
    void analyticsService.logEvent('skip_onboarding', {
      step_name: onboardingStep?.name || 'unknown',
      step_index: onboardingStep?.index ?? 0,
      page_path: location.pathname,
    });
    saveDeferredOnboardingReturn({
      pathname: location.pathname,
      search: location.search,
      state: location.state,
    });
    navigate('/onboarding/phone');
  };

  useEffect(() => {
    if (!onboardingStep) return;

    const startedAtKey = 'booka_onboarding_started_at';
    const existingStartedAt = sessionStorage.getItem(startedAtKey);
    if (!existingStartedAt) {
      sessionStorage.setItem(startedAtKey, String(Date.now()));
      void analyticsService.logEvent('onboarding_started', {
        first_step: onboardingStep.name,
      });
    }

    const enteredAt = Date.now();
    void analyticsService.logEvent('onboarding_step_view', {
      step_name: onboardingStep.name,
      step_index: onboardingStep.index,
      page_path: route,
    });

    return () => {
      void analyticsService.logEvent('onboarding_step_exit', {
        step_name: onboardingStep.name,
        step_index: onboardingStep.index,
        page_path: route,
        duration_seconds: Math.max(0, Math.floor((Date.now() - enteredAt) / 1000)),
      });
    };
  }, [onboardingStep, route]);

  const config = (() => {
    if (route === '/onboarding/topics') {
      return {
        text: 'موضوع مورد علاقت رو انتخاب کن',
        onBack: () => navigate('/welcome'),
        hideBackButton: false,
      };
    }
    if (route === '/onboarding/books') {
      return {
        text: topic ? `${topic}. دوست داری بوکا کدوم کتاب رو برات جادو کنه ؟` : 'دوست داری بوکا کدوم کتاب رو برات جادو کنه ؟',
        onBack: () => navigate('/onboarding/topics'),
        hideBackButton: false,
      };
    }
    if (isOnboardingRead) {
      return {
        text: readGuideText || 'فقط نکات اصلی رو بخون.وقتت رو از سر راه نیاوردی که !',
        onBack: () => navigate(onboardingCompleted ? '/onboarding/phone' : '/onboarding/books', {
          state: currentBookId ? { bookId: currentBookId, disableBookSwitcher: true, fromOnboarding: true } : undefined
        }),
        hideBackButton: false,
      };
    }
    if (isOnboardingNovelRead) {
      return {
        text: normalizedGuideText,
        onBack: () => navigate(onboardingCompleted ? '/onboarding/phone' : '/onboarding/books', {
          state: currentBookId ? { bookId: currentBookId, disableBookSwitcher: true, fromOnboarding: true } : undefined
        }),
        hideBackButton: false,
      };
    }
    if (isOnboardingXpClaim) {
      return {
        text: 'تبریک میگم بهت.یه قسمت رو تموم کردی\nامتیازتو دریافت کن',
        onBack: () => { },
        hideBackButton: true,
      };
    }
    if (isOnboardingStreak) {
      return {
        text: 'ازت میخوام امروز رو فراموش نکنی!\nروزی که معتاد شدی به کتابخونی',
        onBack: () => { },
        hideBackButton: true,
      };
    }
    if (isOnboardingAchievement) {
      return {
        text: 'این نشان  رو با افتخار بهت تقدیم میکنم',
        onBack: () => { },
        hideBackButton: true,
      };
    }
    if (route === '/onboarding/phone') {
      return {
        text: 'میتونم شمارتو داشته باشم؟',
        onBack: returnFromPhone,
        hideBackButton: !deferredReturn,
      };
    }
    if (route === '/onboarding/otp-verification') {
      return {
        text: 'کد تاییدی که برات فرستادیم رو وارد کن',
        onBack: () => navigate('/onboarding/phone'),
        hideBackButton: false,
      };
    }
    if (route === '/onboarding/name') {
      return {
        text: 'میتونم اسمتو بدونم؟',
        // OTP is one-time-use. Going back to it after successful verification
        // would make the user submit an already-consumed code.
        onBack: () => navigate('/onboarding/phone'),
        hideBackButton: false,
      };
    }
    return {
      text: 'با هم ادامه بدیم.',
      onBack: () => navigate('/onboarding/topics'),
      hideBackButton: false,
    };
  })();

  useEffect(() => {
    setOnboardingLastStep(route);
  }, [route]);

  useEffect(() => {
    // Reward screens keep their data in navigation state. After a hard reload
    // that state is gone, so return to the durable checkpoint saved at completion.
    const isReloadedInitialRoute = initialNavigationType === 'reload' && initialRoute === route;
    if (!isReloadedInitialRoute || !onboardingCompleted || route === '/onboarding/phone') return;
    if (isOnboardingReward || isOnboardingRead || isOnboardingNovelRead) {
      navigate('/onboarding/phone', { replace: true });
    }
  }, [initialNavigationType, initialRoute, isOnboardingNovelRead, isOnboardingRead, isOnboardingReward, navigate, onboardingCompleted, route]);

  useEffect(() => {
    const readHeaderHeight = () => {
      const header = document.getElementById('onboarding-guide-header');
      if (!header) return;
      const h = Math.ceil(header.getBoundingClientRect().height);
      if (h > 0) setHeaderHeight(h);
    };

    readHeaderHeight();
    const header = document.getElementById('onboarding-guide-header');
    let observer: ResizeObserver | null = null;
    if (header && 'ResizeObserver' in window) {
      observer = new ResizeObserver(() => readHeaderHeight());
      observer.observe(header);
    }
    window.addEventListener('resize', readHeaderHeight);
    const timer1 = window.setTimeout(readHeaderHeight, 60);
    const timer2 = window.setTimeout(readHeaderHeight, 220);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', readHeaderHeight);
      window.clearTimeout(timer1);
      window.clearTimeout(timer2);
    };
  }, [route]);

  useEffect(() => {
    const handler = (event: Event) => {
      const custom = event as CustomEvent<{ text?: string; replay?: boolean }>;
      if (custom.detail?.text === undefined) return;
      const text = custom.detail.text;
      setReadGuideText(text);
      if (custom.detail?.replay) {
        setReadGuideReplayKey((prev) => prev + 1);
      }
    };

    window.addEventListener('onboarding-read-guide', handler as EventListener);
    return () => window.removeEventListener('onboarding-read-guide', handler as EventListener);
  }, []);

  // Event listener برای تغییر دیالوگ دانا در صفحه path
  useEffect(() => {
    const handler = (event: Event) => {
      const custom = event as CustomEvent<{ text?: string }>;
      if (custom.detail?.text === undefined) return;
      const text = custom.detail.text;
      setReadGuideText(text);
    };

    window.addEventListener('onboarding-path-guide', handler as EventListener);
    return () => window.removeEventListener('onboarding-path-guide', handler as EventListener);
  }, []);

  useEffect(() => {
    if (!isOnboardingRead && !isOnboardingNovelRead) {
      setReadGuideText('');
    }
  }, [isOnboardingRead, isOnboardingNovelRead]);

  useEffect(() => {
    const deferredPhoneReturn = route === '/onboarding/phone' ? getDeferredOnboardingReturn() : null;
    const shouldBlockBack = isOnboardingXpClaim || isOnboardingStreak || isOnboardingAchievement ||
      (onboardingCompleted && route.startsWith('/onboarding/') && !deferredPhoneReturn) ||
      (route === '/onboarding/phone' && !deferredPhoneReturn);

    if (!shouldBlockBack && !deferredPhoneReturn) return;

    // Keep browser back behavior deterministic after completing onboarding,
    // on protected reward screens, and on the deferred phone screen.
    window.history.pushState(null, '', window.location.href);

    const handlePopState = () => {
      if (deferredPhoneReturn) {
        const savedReturn = getDeferredOnboardingReturn();
        if (savedReturn) {
          clearDeferredOnboardingReturn();
          navigate(getDeferredOnboardingUrl(savedReturn), {
            replace: true,
            state: savedReturn.state,
          });
          return;
        }
      }

      window.history.pushState(null, '', window.location.href);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isOnboardingXpClaim, isOnboardingStreak, isOnboardingAchievement, onboardingCompleted, route, navigate]);

  return (
    <div
      className="min-h-screen bg-white"
      style={{ ['--onboarding-header-h' as any]: `${headerHeight}px` }}
    >
      <OnboardingGuideHeader 
        text={config.text} 
        onBack={config.onBack} 
        progressStep={getOnboardingProgressStep(route)}
        progressTotal={8}
        replayKey={readGuideReplayKey}
        hideBackButton={config.hideBackButton}
        showLaterButton={canDeferRegistration}
        onLater={deferRegistration}
      />
      <Outlet />
    </div>
  );
};

export default OnboardingLayout;
